import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { UserModel, UserSalesLocation } from '../models/user.model';

import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import * as moment from 'moment';
import { UserService } from './user.service';
import { LanguagePipe } from '../pipes/language.pipe';
import { LanguageService } from './language.service';
import { el } from '@fullcalendar/core/internal-common';
export type UserType = UserModel | undefined;
export type UserSalesLocationType = UserSalesLocation | undefined;
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    currentUser$: Observable<UserType>;
    isLoading$: Observable<boolean>;
    currentUserSubject: BehaviorSubject<UserType | undefined>;
    currentSalesLocation$: Observable<UserSalesLocationType>;
    currentSalesLocationSubject: BehaviorSubject<UserSalesLocationType>;
    isLoadingSubject: BehaviorSubject<boolean>;
    newEmail: string = '';
    userRole: string = '';
    private permissionSubject = new BehaviorSubject<any>({});
    permission$ = this.permissionSubject.asObservable();

    reloadCurrentRoute() {
        const currentUrl = this.router.url;
        this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
                this.router.navigate([currentUrl]);
            });
    }

    hasPermission(permission: string, permissionType: string): boolean {
        if (this.permissionSubject?.value?.userRole === 'admin') {
            return true;
        }
        if (
            this.permissionSubject?.value?.[permission]?.children?.[
                permissionType
            ]?.access === undefined &&
            this.permissionSubject?.value?.[permission]?.children?.[
                permissionType
            ]?.access === undefined &&
            this.permissionSubject?.value?.[permission][permissionType] ===
                undefined
        ) {
            return false;
        }

        if (this.userRole === 'staff') {
            const main =
                this.permissionSubject?.value?.[permission]?.[permissionType];
            if (
                main === 'partial' ||
                main === 'full' ||
                main === 'view' ||
                main === 'edit' ||
                main === true
            ) {
                return true;
            }
            const child =
                this.permissionSubject?.value?.[permission]?.children?.[
                    permissionType
                ]?.access;
            if (
                child === 'partial' ||
                child === 'full' ||
                child === 'view' ||
                child === 'edit' ||
                child === true
            ) {
                return true;
            } else {
                return false;
            }
        }

        if (this.userRole === 'user') {
            const main =
                this.permissionSubject?.value?.[permission]?.[permissionType];
            if (
                main === 'partial' ||
                main === 'full' ||
                main === 'view' ||
                main === 'edit' ||
                main === true
            ) {
                return true;
            }
            const child =
                this.permissionSubject?.value?.[permission]?.children?.[
                    permissionType
                ]?.access;
            if (
                child === 'partial' ||
                child === 'full' ||
                child === 'view' ||
                child === 'edit' ||
                child === true
            ) {
                return true;
            } else {
                return false;
            }
        }
    }

    getPermissions() {
        const currentPermissions = this.permissionSubject.value;
        if (this.permission$.subscribe()) {
            const adminPermissions = this.permissionSubject?.value;
            adminPermissions['userRole'] = this.userRole;
            return adminPermissions;
        }

        return {};
    }

    get currentUserValue(): UserType {
        return this.currentUserSubject.value;
    }

    set currentUserValue(user: UserType) {
        this.currentUserSubject.next(user);
    }
    get currentSalesLocationValue(): UserSalesLocationType {
        return this.currentSalesLocationSubject.value;
    }

    constructor(
        private router: Router,
        private http: HttpClient,
        private afAuth: AngularFireAuth,
        private userService: UserService,
        private languagePipe: LanguagePipe,
        private languageProvider: LanguageService
    ) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(true);
        this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
        this.currentUser$ = this.currentUserSubject.asObservable();
        this.currentSalesLocationSubject =
            new BehaviorSubject<UserSalesLocationType>(undefined);
        this.currentSalesLocation$ =
            this.currentSalesLocationSubject.asObservable();
        this.isLoading$ = this.isLoadingSubject.asObservable();
        // Ensure long-lived session so MFA isn't re-prompted within 30 days unless user signs out
        this.afAuth
            .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
            .catch(() => {});
        // this.initializeAuth(); // 👈 Now reusable
        this.afAuth.authState.subscribe((aUser) => {
            if (!aUser) {
                this.currentUserSubject.next(undefined);
                this.isLoadingSubject.next(false);
                return;
            }
            let user: UserModel = { ...aUser['_delegate'], data: [] };

            this.getUserData(user.uid).subscribe((data) => {
                user.data = data;
                this.currentUserSubject.next(user);
                this.isLoadingSubject.next(false);
            });
        });
        this.currentSalesLocation$.subscribe((x) => {
            if (!x) return;
            let user = this.currentUserSubject.value;
            if (user && user.email) {
                window['Intercom']('boot', {
                    app_id: 'hm8td4yv',
                    name: `${x.salesLocationName} // ${x.salesLocationRoute}`, // Full name
                    email: user?.email, // Email address
                    created_at: moment(user.metadata.creationTime).unix(), // Signup date as a Unix timestamp
                });
                window['Intercom']('update');

                this.assignRole(
                    this.currentUserSubject.value.uid,
                    x.salesLocationId
                );
            }
        });
    }
    async login(email: string, password: string) {
        this.isLoadingSubject.next(true);
        try {
            let userCredential = await this.afAuth.signInWithEmailAndPassword(
                email,
                password
            );
            return userCredential;
        } catch (error: any) {
            this.isLoadingSubject.next(false);
            if (
                error?.code === 'auth/wrong-password' ||
                error?.code === 'auth/user-not-found'
            ) {
                return this.languagePipe.transform(
                    'Invalid email or password',
                    'INVALID_EMAIL_OR_PASSWORD',
                    this.languageProvider.currentLanguage
                );
            }
            // return this.languagePipe.transform(
            //     'Invalid email or password',
            //     'INVALID_EMAIL_OR_PASSWORD',
            //     this.languageProvider.currentLanguage
            // );
            throw error;
        }
    }
    getUserData(userId: string): Observable<any> {
        return this.http.get<any[]>(
            `${environment.pubQueueApiUrlEU}/v1/customers/${userId}`,
            { observe: 'body', responseType: 'json' }
        );
    }

    async assignRole(userId, unitId) {
        await this.getPermissionsforUser(userId, unitId).then((data) => {
            if (data['message']) {
                this.userRole = 'admin';
                this.permissionSubject.next(this.getPermissions());

                return;
            } else {
                this.userRole = data['name'];

                data['permissions']['userRole'] = this.userRole;
                this.permissionSubject.next(data['permissions']);
            }
        });
    }
    async getPermissionsforUser(userId, unitId) {
        let body = {
            unitId,
        };

        return await this.http
            .post<any[]>(
                `${environment.pubQueueApiUrlEU}/v1/userpermissions/${userId}`,
                body
            )
            .toPromise();
    }
    logout() {
        this.afAuth.signOut();
        this.router.navigate([''], {
            queryParams: {},
        });
    }
    async resetPassword(email: string) {
        this.languageProvider.currentLanguage
        this.newEmail = email;
        // return await this.afAuth.sendPasswordResetEmail(email);
        return await firstValueFrom(this.http.post(`${environment.pubQueueApiUrl}/api/v1/auth/resetpw`, { email , languageCode:this.languageProvider.currentLanguage}))
    }

    // need create new user then login
    registration(user: UserModel) {
        this.isLoadingSubject.next(true);
    }

    async forgotPassword(email: string) {
        this.isLoadingSubject.next(true);
        try {
            await this.afAuth.sendPasswordResetEmail(email);
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingSubject.next(false);
        }
    }
    selectSalesLocation(salesLocation: UserSalesLocationType) {
        this.currentSalesLocationSubject.next(salesLocation);
        let unit = {
            id: salesLocation.salesLocationId,
            info: {
                address: salesLocation.salesLocationAddress,
                city: salesLocation.salesLocationCity,
                companyName: salesLocation.companyName,
                email: salesLocation.salesLocationEmail,
                logoUrl: '',
                name: salesLocation.salesLocationName,
                orgNo: salesLocation.companyNumber,
                phoneNumber: salesLocation.salesLocationPhone,
                zipCode: salesLocation.salesLocationPostalCode,
            },
            name: salesLocation.salesLocationName,
            routeName: salesLocation.salesLocationRoute,
        };
        this.userService.selectedUnit$.next(unit);
        this.userService.selectedUnit2$.next(unit);
        this.userService.selectedOrganisation$.next({});
    }

    async updatePassword(email, password) {
        let body = {
            email,
            password,
        };
        return await this.http
            .post<any[]>(
                `${environment.pubQueueApiUrlEU}/v1/units/users/setNewPassword`,
                body
            )
            .toPromise();
    }

}
